const TENANT: string = (import.meta.env.VITE_TENANT as string | undefined) ?? 'flightstandards';

export interface RefreshResult {
  access_token: string;
  user: unknown; // AuthUser shape — consumers cast as needed (avoids a circular import)
  expires_in?: number;
}

// Step 5d Phase 1: callers MUST distinguish "the server rejected the session" from "the
// server could not be reached". Only an explicit 401/403 logs the user out; a network
// failure (or a 5xx/empty-bodied response from a hung backend) keeps the last-known
// session state so an offline device can still reach its cached board. Token validity
// is unchanged — this is purely "no-network is not the same as not-authenticated".
export type RefreshOutcome =
  | { ok: true; result: RefreshResult }
  | { ok: false; reason: 'rejected' | 'unavailable' };

// ─── Single-flight session refresh ───────────────────────────────────────────
// A burst of concurrent 401s — e.g. EVERY board fetch on a hard load, where the
// in-memory access token is gone — must share ONE in-flight /api/auth/refresh. Firing
// one refresh per 401 trips the backend's single-use rotation: only the first rotates;
// the rest present the now-revoked token → reuse-detection revokes the whole token_family
// (`UPDATE refresh_tokens … WHERE token_family`), and ~9 concurrent same-family revocations
// contend on those rows, starving the connection pool until the board (and everything)
// wedges. (Mechanism confirmed by direct Prisma-debug capture — HANDOFF Session 22.)
//
// While a refresh is in flight, all callers await the SAME promise; it's cleared on settle
// (success OR failure) so the next genuine refresh can proceed.
let inflight: Promise<RefreshOutcome> | null = null;

const UNAVAILABLE: RefreshOutcome = { ok: false, reason: 'unavailable' };

async function doRefresh(): Promise<RefreshOutcome> {
  let res: Response;
  try {
    res = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Tenant': TENANT },
    });
  } catch {
    return UNAVAILABLE; // network-level failure — server never answered
  }
  // ONLY an explicit auth rejection counts as 'rejected'. Anything else a reached-but-
  // unhealthy backend produces (5xx — incl. the vite proxy's 500 when the backend is
  // down in dev, S29 Part C) is 'unavailable': it must not log the user out.
  if (res.status === 401 || res.status === 403) return { ok: false, reason: 'rejected' };
  if (!res.ok) return UNAVAILABLE;
  // Defensive body read (cf. 146deac): an empty/non-JSON body (e.g. an empty 500 from a
  // hung backend) is 'unavailable' rather than throwing "Unexpected end of JSON input".
  let text = '';
  try { text = await res.text(); } catch { return UNAVAILABLE; }
  if (!text.trim()) return UNAVAILABLE;
  let body: { data: RefreshResult | null; error: string | null } | null = null;
  try { body = JSON.parse(text) as { data: RefreshResult | null; error: string | null }; } catch { return UNAVAILABLE; }
  if (!body || body.error || !body.data?.access_token) return UNAVAILABLE;
  return { ok: true, result: body.data };
}

// Concurrent callers share one in-flight refresh. Returns the refresh outcome — token
// storage / user state / logout decisions are handled by the caller.
export function refreshSession(): Promise<RefreshOutcome> {
  if (inflight) return inflight;
  inflight = doRefresh().finally(() => { inflight = null; });
  return inflight;
}
